import { useEffect, useState } from "react";
import { Loader2, LifeBuoy, Send, StickyNote, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/adminApi";
import { StatusBadge } from "@/components/admin/_shared";
import { toast } from "sonner";

const TYPES = ["enquiry", "feature_request", "bug_report", "billing", "technical_support", "sales", "other"];
const STATUSES = ["open", "in_progress", "waiting_for_customer", "resolved", "closed"];
const TEAM = ["Super Admin", "Support Agent", "Sales Agent", "Operations"];
const label = (s) => (s || "").replace(/_/g, " ");

export default function AdminSupport() {
  const [tickets, setTickets] = useState(null);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    const r = await adminApi.get("/admin/tickets");
    setTickets(r.data.tickets);
    if (sel) setSel(r.data.tickets.find((t) => t.id === sel.id) || null);
  };
  useEffect(() => { load(); }, []);

  const patch = async (field, value) => {
    const r = await adminApi.patch(`/admin/tickets/${sel.id}`, { [field]: value });
    setSel(r.data); load(); toast.success("Ticket updated");
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    const r = await adminApi.post(`/admin/tickets/${sel.id}/reply`, { text: reply });
    setSel(r.data); setReply(""); load(); toast.success("Reply sent to customer");
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const r = await adminApi.post(`/admin/tickets/${sel.id}/note`, { text: note });
    setSel(r.data); setNote(""); load(); toast.success("Internal note added");
  };

  if (!tickets) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-support">
      <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><LifeBuoy className="h-6 w-6 text-blue-600" /> Support Centre</h1>
      <p className="text-slate-500 mt-1 text-sm">Every contact form submission becomes a ticket.</p>
      <div className="mt-5 grid lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden lg:col-span-1">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold">{tickets.length} tickets</div>
          <div className="divide-y divide-slate-50 max-h-[70vh] overflow-y-auto">
            {tickets.length === 0 && <p className="px-4 py-8 text-sm text-slate-400 text-center">No tickets yet</p>}
            {tickets.map((t) => (
              <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setSel(t)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${sel?.id === t.id ? "bg-blue-50" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{t.name}</p>
                  <StatusBadge status={t.status} />
                </div>
                <p className="text-xs text-slate-500 truncate">{t.subject}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{label(t.type)}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!sel ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white h-full flex items-center justify-center py-20 text-slate-400 text-sm">Select a ticket to view details</div>
          ) : (
            <div data-testid="ticket-detail" className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
              <div>
                <h2 className="font-heading font-bold tracking-tight">{sel.name}</h2>
                <p className="text-sm text-slate-500">{sel.email}{sel.phone ? ` · ${sel.phone}` : ""}</p>
                <p className="mt-3 text-sm bg-slate-50 rounded-lg p-3">{sel.message}</p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div><p className="text-xs text-slate-400 mb-1">Type</p>
                  <Select value={sel.type} onValueChange={(v) => patch("type", v)}>
                    <SelectTrigger data-testid="ticket-type" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{label(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><p className="text-xs text-slate-400 mb-1">Status</p>
                  <Select value={sel.status} onValueChange={(v) => patch("status", v)}>
                    <SelectTrigger data-testid="ticket-status" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((t) => <SelectItem key={t} value={t} className="capitalize">{label(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><p className="text-xs text-slate-400 mb-1">Assignee</p>
                  <Select value={sel.assignee || "unassigned"} onValueChange={(v) => patch("assignee", v)}>
                    <SelectTrigger data-testid="ticket-assignee" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{TEAM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Replies */}
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Conversation</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(sel.replies || []).map((r) => <div key={r.id} className="text-sm bg-blue-50 rounded-lg p-2"><span className="font-semibold text-xs text-blue-700">{r.by}</span><p>{r.text}</p></div>)}
                  {(sel.replies || []).length === 0 && <p className="text-xs text-slate-400">No replies yet.</p>}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…" />
                  <Button data-testid="ticket-reply-send" onClick={sendReply} className="bg-blue-600 hover:bg-blue-700 gap-1"><Send className="h-4 w-4" /> Send</Button>
                </div>
              </div>

              {/* Internal notes */}
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Lock className="h-3 w-3" /> Internal notes (customer can't see)</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {(sel.notes || []).map((n) => <div key={n.id} className="text-sm bg-amber-50 rounded-lg p-2"><span className="font-semibold text-xs text-amber-700">{n.by}</span><p>{n.text}</p></div>)}
                  {(sel.notes || []).length === 0 && <p className="text-xs text-slate-400">No notes.</p>}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input data-testid="ticket-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" />
                  <Button data-testid="ticket-note-add" onClick={addNote} variant="outline" className="gap-1"><StickyNote className="h-4 w-4" /> Note</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
